/*
 * @author: wayou
 * @date: 2018-09-10 18:06:35
 * @description: 播放器的控制面板
 */
import { lang } from '@umu/ukit-lang';
import React, { Component } from 'react';
import { classSet } from '@umu/umu-dom';
import USubtitles, { SubtitleItem } from '@umu/umu-subtitle';
import { DRAGGABLE_TYPE, TOOGLE_TYPE } from '@umu/umu-types';
import MediaTimeDisplay from './components/mediaTimeDisplay/mediaTimeDisplay';
import RangeSlider from './components/rangeSlider/rangeSlider';
import { toggleAppNavBar } from '../../services/webview/common';
import './playControl.scss';
import { ISubtitleRes } from '@umu/umu-audio-slides';
import { LANG_TEXT_MAP } from '@umu/umu-lang-switch';
import MoreSetting from '../@playerPlugin/moreSetting/moreSetting';
import AISubtitleSetting from '../@playerPlugin/aiSubtitleSetting/aiSubtitleSetting';
import {emitCustomEvent} from "../@playerPlugin/emitCustomEvent"

interface IPlayControlProps {
    /**
     * 当前媒体的播放时间 (ms)
     */
    time: number;

    /**
     * 音频时长 (ms)
     */
    duration: number;

    /**
     * 已缓冲的时长 (ms)
     */
    buffered?: number;

    /**
     * 是否正在播放
     */
    isPlaying: boolean;

    /**
     * 是否支持倍速播放
     */
    isSupportPlaybackRate: boolean;

    /**
     * 进度条是否可拖动
     */
    isDraggable?: boolean;

    /**
     * 进度条拖动类型
     */
    draggableType?: DRAGGABLE_TYPE;

    /**
     * 最大播放时间
     */
    maxPlayedTime?: number;

    /**
     * 是否展示进度条，默认展示
     */
    isShowProgressBar?: boolean;

    /**
     * 是否只展示播放进行中的时间而不是 mm:ss/mm:ss
     */
    displaySingleTime?: boolean;

    /**
     * 是否展示倍速设置，默认展示
     */
    isShowPlaybackRate?: boolean;

    /**
     * 是否展示全屏设置，默认展示
     */
    isShowFullScreen?: boolean;

    /**
     * 播放点击
     */
    play?: (isResume?: boolean) => void;

    /**
     * 暂停点击
     */
    pause?: VoidFunction;

    /**
     * 在判断是否可以seek后执行操作
     */
    beforeSeek?: (canSeek: boolean) => void;
    /**
     * 进度条拖动
     */
    seek?: (percentage: number) => void;

    /**
     * 是否全屏
     */
    isFullscreen?: boolean;

    /**
     * 倍率
     */
    rate: number;

    /**
     * 设置倍率
     */
    setRate?: (rate: number) => void;

    /**
     * 切换全屏
     */
    toggleFullScreen?: (isShow: boolean) => void;

    /**
     * 内容区域dom
     */
    contentDom?: string | Element;
    // 目标类型
    targetType?: 'video' | 'radio' | 'other';
    subtitle?: ISubtitleRes;
    aiSubtitleConfig?: {
        isNotSaveTranslateSetting?: boolean,
        translateSettingShow?: {
            isHiddenFontSize?: boolean;
            isHiddenTransPos?: boolean;
            isHiddenOriginalPos?: boolean;
            isHiddenSetupSetting?: boolean;
            isHiddenApplyToLearner?: boolean;
            isHiddenSubtitleSetting?: boolean;
        }
    },
    uPlayerWrapContainer?: any;
    isShowMoreSetting?: boolean;
    subtitleUrl?: string;
}

interface IStates {
    isPlaying: boolean;
    isShowSubtitle: boolean;
}

enum PAUSED_STATUS {
    unknow = 1,
    paused = 2,
    resumed = 3,
}

type LANG_TEXT_MAP_KEY = keyof typeof LANG_TEXT_MAP;

class PlayControl extends Component<IPlayControlProps, IStates> {
    public static defaultProps: Partial<IPlayControlProps> = {
        time: 0,
        rate: 1,
        buffered: 0,
        isSupportPlaybackRate: false,
        isDraggable: true,
        isShowProgressBar: true,
        isShowPlaybackRate: true,
        isShowFullScreen: true,
    };

    public pausedStatus = PAUSED_STATUS.unknow;
    public uSubtitles?: USubtitles;
    public wrapContainer?: any;
    public uPlayerWrapContainer?: any;

    constructor(props: IPlayControlProps) {
        super(props);
        this.state = {
            isPlaying: false,
            isShowSubtitle: false,
        };
    }

    private getUPlayerWrapContainerEle() {
        const { contentDom, uPlayerWrapContainer } = this.props;
        if (uPlayerWrapContainer) {
            return uPlayerWrapContainer;
        }

        if (typeof contentDom === 'string' && contentDom) {
            return document.querySelector(contentDom);
        }

        return undefined;
    }

    public componentDidMount() {
        this.wrapContainer = document.querySelector('.play-control.audio-control');
        this.uPlayerWrapContainer = this.getUPlayerWrapContainerEle();

        this.addEventlistenerClickEvent();
    }

    public componentDidUpdate(prevProps: IPlayControlProps) {
        if (
            (this.props.subtitle && this.props.subtitle !== prevProps.subtitle) ||
            (this.props.subtitleUrl && this.props.subtitleUrl !== prevProps.subtitleUrl)
        ) {
            this.initSubtitle();
        }
        if (this.props.time !== 0 && prevProps.time === 0) {
            // 开始播放
            this.setState({
                isPlaying: true,
            });
        }
        if (prevProps.time !== this.props.time && this.uSubtitles) {
            this.uSubtitles.timeUpdate(this.props.time);
        }
    }

    public render() {
        return (
            <div className="play-control audio-control">
                <div className="media-control">
                    {this.renderMediaToggler()}
                    {this.renderPlayingProgress()}
                    {this.renderPlayingTime()}
                    {this.renderSubtitleControl()}
                    {this.renderSpeedControl()}
                    {this.renderFullScreenButton()}
                </div>
            </div>
        );
    }

    private renderPlayingTime = () => {
        const { time, duration, displaySingleTime } = this.props;
        return (
            <div className="media-time">
                <MediaTimeDisplay time={time} duration={duration} displaySingleTime={displaySingleTime} />
            </div>
        );
    }

    private renderPlayingProgress = () => {
        const { time, buffered, isDraggable, isShowProgressBar } = this.props;
        const percentage = this.getPercentage(time);
        const bufferedPercentage = this.getPercentage(buffered!);
        return (
            <div className="media-indicator">
                {isShowProgressBar ? (
                    <RangeSlider
                        disable={!isDraggable}
                        percentage={percentage}
                        buffered={bufferedPercentage}
                        onDrag={this.onDragging}
                    />
                ) : null}
            </div>
        );
    }

    private renderFullScreenButton = () => {
        const { isShowFullScreen } = this.props;
        const isPageInFullscreen = !!this.props.isFullscreen;
        if (!isShowFullScreen) {
            return null;
        }
        return (
            <div className="toggle-media-fullscreen">
                <span
                    className={classSet({
                        'icon icon-fullscreen': !isPageInFullscreen,
                        'icon icon-fullscreen-smaller': isPageInFullscreen,
                    })}
                    onClick={this.onFullScreenClick}
                />
            </div>
        );
    }

    public getTranslateSubtitle = (): SubtitleItem[] => {
        const { subtitle } = this.props;

        if (!subtitle) {
            return [];
        }

        const {
            file_url,
            setup: { lang: _lang },
            trans_lang,
        } = subtitle;

        const originalItem = {
            lang: _lang,
            type: 1,
            file_url,
            status: '2',
            label: LANG_TEXT_MAP[_lang as LANG_TEXT_MAP_KEY] + lang('（原始语言）'),
        };

        const list = trans_lang?.list || [];
        const translateList = list.map((item: SubtitleItem) => ({
            ...item,
            label: LANG_TEXT_MAP[item.lang as LANG_TEXT_MAP_KEY],
        }));

        return [originalItem, ...translateList];
    };

    private getSetupLang = (subtitle:any) => {
        const trans_lang = this.getTransLang(subtitle);
        return trans_lang.length ? trans_lang[0] : subtitle?.setup?.lang;
    };

    public getTranslateSetting() {
        const { subtitle, aiSubtitleConfig } = this.props;
        if (aiSubtitleConfig?.isNotSaveTranslateSetting) {
            return {
                trans_pos: 1,
                font_size: 3,
                original_pos: 1,
                display_original: 0,
                apply_to_learner: 0,
                lang: subtitle?.setup.lang,
                setup_lang: this.getSetupLang(subtitle),
                trans_lang: subtitle?.setup?.trans_lang || [],
            }
        }
        return subtitle?.setup || {}
    }

    private computedSubtitleUrl() {
        const { subtitle, subtitleUrl } = this.props;
        return subtitleUrl || subtitle?.file_url || '';
    }

    private initSubtitle() {
        const { subtitle, time, contentDom, targetType = 'other' } = this.props;
        const subtitleUrl = this.computedSubtitleUrl();

        if (subtitleUrl) {
            this.uSubtitles = new USubtitles({
                wrapContainer: contentDom,
                resUrl: subtitleUrl,
                targetType,
                isShow: true,
                subtitleInfo: subtitle || {},
                translateSetting: this.getTranslateSetting(),
                translateSubtitle: this.getTranslateSubtitle(),
            });
            this.uSubtitles.timeUpdate(time);
        } else {
            this.uSubtitles = undefined;
        }
        this.setState({ isShowSubtitle: !!subtitleUrl });
    }

    private getTransLang = (subtitle: any) => {
        const trans_lang = subtitle?.setup?.trans_lang;
        return Array.isArray(trans_lang) ? trans_lang : [];
    };

    private getUControlBar = () => {
        const { subtitle, aiSubtitleConfig, isSupportPlaybackRate, isShowPlaybackRate } = this.props;

        const translateSetting = this.getTranslateSetting();
        const translateSettingShow = aiSubtitleConfig?.translateSettingShow || {};

        const uControlBar = {
            config: {
                subtitleInfo: subtitle,
                subtitleConfig: {
                    from: 'angular',
                    translateSettingShow,
                    translateSetting: translateSetting,
                    isNotSaveTranslateSetting: aiSubtitleConfig?.isNotSaveTranslateSetting,
                },
                sessionId: subtitle?.session_id,
                speedOptions: [0.8, 1.0, 1.2, 1.5, 2.0],
                hasSubtitle: !!this.uSubtitles,
                hasSpeed: isSupportPlaybackRate && isShowPlaybackRate,
            },
            wrapContainer: this.uPlayerWrapContainer,
        }

        return uControlBar;
    }


    private renderSubtitleControl() {
        const { isShowMoreSetting } = this.props;

        const uControlBar = this.getUControlBar();

        if (this.uSubtitles) {
            return isShowMoreSetting ? (
                <MoreSetting uControlBar={uControlBar} />
            ) : (
                <AISubtitleSetting uControlBar={uControlBar} isInMoreSetting={false} />
            );
        }

        return null;
    }

    private setAppNavBar = () => {
        toggleAppNavBar(!this.props.isFullscreen ? TOOGLE_TYPE.HIDE : TOOGLE_TYPE.SHOW);
    };

    private getPercentage(val: number) {
        const { duration } = this.props;
        return val / duration;
    }

    private onDragging = (percentage: number) => {
        const { seek } = this.props;

        if (!this.onBeforeDragging(percentage)) {
            return;
        }
        if (seek) {
            seek(percentage);
        }
    };

    private onBeforeDragging = (percentage: number) => {
        const { isDraggable, draggableType, beforeSeek, maxPlayedTime } = this.props;
        if (!isDraggable) {
            beforeSeek && beforeSeek(false);
            return false;
        }
        let canSeek = true;
        if (draggableType === DRAGGABLE_TYPE.DRAGGABLE_PLAYED) {
            const maxPercentage = this.getPercentage((maxPlayedTime || 0) * 1000);
            canSeek = percentage <= maxPercentage;
        }
        beforeSeek && beforeSeek(canSeek);
        return canSeek;
    };

    private renderMediaToggler() {
        const { isPlaying } = this.props;
        return (
            <div className="toggle-media-play">
                {isPlaying ? (
                    <span className="icon icon-pause" onClick={this.onPauseClick} />
                ) : (
                    <span className="icon icon-play" onClick={this.onPlayClick} />
                )}
            </div>
        );
    }

    private renderSpeedControl() {
        const { contentDom, isShowMoreSetting, isSupportPlaybackRate, isShowPlaybackRate } = this.props;

        const uControlBar = {
            config: {
                hasSubtitle: false,
                speedOptions: [0.8, 1.0, 1.2, 1.5, 2.0],
                hasSpeed: isSupportPlaybackRate && isShowPlaybackRate,
            },
            wrapContainer: typeof contentDom === 'string' ? document.querySelector(contentDom as string): contentDom,
        }

        const isShowSpeedControl = ((isShowMoreSetting != undefined && !isShowMoreSetting) ||  !this.uSubtitles) && this.uPlayerWrapContainer;

        if (isShowSpeedControl) {
            return (
                <MoreSetting uControlBar={ uControlBar }/>
            );
        } else {
            return null;
        }
    }

    private onPlayClick = () => {
        const { play } = this.props;
        if (play) {
            play(this.pausedStatus === PAUSED_STATUS.paused);
        }
        this.setState({
            isPlaying: true,
        });
        if (this.pausedStatus === PAUSED_STATUS.paused) {
            this.pausedStatus = PAUSED_STATUS.resumed;
        }
    };

    private onPauseClick = () => {
        const { pause } = this.props;
        if (pause) {
            pause();
        }
        this.setState({
            isPlaying: false,
        });
        if (this.pausedStatus === PAUSED_STATUS.unknow || this.pausedStatus === PAUSED_STATUS.resumed) {
            this.pausedStatus = PAUSED_STATUS.paused;
        }
    };

    private onRateChange = (rate: number) => {
        const { setRate } = this.props;
        if (setRate) {
            setRate(rate);
        }
    };

    private onFullScreenClick = () => {
        const { toggleFullScreen } = this.props;
        this.setAppNavBar();
        if (toggleFullScreen) {
            toggleFullScreen(true);
        }
    };

    public addEventlistenerClickEvent = () => {
        window.addEventListener(
            'click',
            (e: any) => {
                // M 端 Drawer 元素
                const uPlayerMoreSettingTrigger = document.querySelector('.u-custom-more-setting');
                const uPlayerMoreSettingPanel = document.querySelector('.u-player-more-setting-panel');
                const uPlayerTrigger = document.querySelector('.u-ai-subtitle-translate-setting-icon');
                // Pc 端 Popover 元素
                const uPlayerPopover = document.querySelector(
                    '.u-ai-subtitle-translate-setting-popover .umu-design-comp-popover-inner',
                );

                if (
                    uPlayerTrigger &&
                    uPlayerPopover &&
                    !uPlayerPopover.contains(e.target) &&
                    !uPlayerTrigger.contains(e.target)
                ) {
                    emitCustomEvent('u-click:popover-emptySpace', this.uPlayerWrapContainer, {});
                }

                if (
                    uPlayerMoreSettingPanel &&
                    !uPlayerMoreSettingPanel?.contains(e.target) &&
                    !uPlayerMoreSettingTrigger?.contains(e.target)
                ) {
                    emitCustomEvent('u-click:drawer-emptySpace', this.uPlayerWrapContainer, {});
                }
            },
            true,
        );

        this.uPlayerWrapContainer?.addEventListener('u-select:speed', (e: any) => {
            const { value } = e.detail;
            this.onRateChange(value);
        });
    }

    public controlBarEmitSubtitleToVideo = (value: any)=>{
        emitCustomEvent('u-edit:subtitle_to_video', this.wrapContainer, { value });
    }
}

export default PlayControl;
